import { useEffect, useRef } from 'react';

import { useAppStore } from '../../../store/useAppStore';
import type { CostumePiece, CostumeSizingProfile, Fitting, MeasurementProfile, QuickChangeTransition } from '../../costumes/types';
import {
    subscribeToCostumePieces,
    subscribeToCostumeSizingProfiles,
    subscribeToFittings,
    subscribeToMeasurementProfiles,
    subscribeToOwnCostumeSizingProfile,
    subscribeToQuickChangeTransitions,
} from '../../costumes/costumes-api';
import {
    clearAcknowledgedPendingCostumeFittingDeletes,
    clearAcknowledgedPendingCostumeMeasurementDeletes,
    clearAcknowledgedPendingCostumePieceDeletes,
    clearAcknowledgedPendingCostumeQuickChangeDeletes,
    clearAcknowledgedPendingCostumeSizingDeletes,
    clearPendingCostumeFittingWrite,
    clearPendingCostumeMeasurementWrite,
    clearPendingCostumePieceWrite,
    clearPendingCostumeQuickChangeWrite,
    clearPendingCostumeSizingWrite,
    isPendingCostumeFittingDelete,
    isPendingCostumeFittingWrite,
    isPendingCostumeMeasurementDelete,
    isPendingCostumeMeasurementWrite,
    isPendingCostumePieceDelete,
    isPendingCostumePieceWrite,
    isPendingCostumeQuickChangeDelete,
    isPendingCostumeQuickChangeWrite,
    isPendingCostumeSizingDelete,
    isPendingCostumeSizingWrite,
} from '../costume-pending-writes';
import type { ProjectScope } from '../project-scope';

function isUnsubscribe(value: unknown): value is () => void {
    return typeof value === 'function';
}

function normalizePendingComparisonValue(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(normalizePendingComparisonValue);
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .filter(([, entryValue]) => entryValue !== undefined)
                .toSorted(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
                .map(([key, entryValue]) => [key, normalizePendingComparisonValue(entryValue)])
        );
    }
    return value;
}

function recordsMatchForPendingWrite(left: unknown, right: unknown): boolean {
    return JSON.stringify(normalizePendingComparisonValue(left))
        === JSON.stringify(normalizePendingComparisonValue(right));
}

function appearancePatch(
    appearance: MeasurementProfile['appearance']
): Partial<Pick<MeasurementProfile, 'appearance'>> {
    return appearance === undefined ? {} : { appearance };
}

function omitAppearance(profile: MeasurementProfile): Omit<MeasurementProfile, 'appearance'> {
    const { appearance: _appearance, ...profileWithoutAppearance } = profile;
    void _appearance;
    return profileWithoutAppearance;
}

function remoteTimestampHasCaughtUp(remoteTimestamp?: number, localTimestamp?: number): boolean {
    return typeof remoteTimestamp === 'number'
        && typeof localTimestamp === 'number'
        && remoteTimestamp >= localTimestamp;
}

function sizingProfileAcknowledgesMeasurement(
    sizingProfile: CostumeSizingProfile,
    measurement?: MeasurementProfile
): boolean {
    return !measurement
        || remoteTimestampHasCaughtUp(sizingProfile.lastUpdated, measurement.lastUpdated)
        || recordsMatchForPendingWrite(
            {
                sizing: sizingProfile.sizing ?? {},
                ...appearancePatch(sizingProfile.appearance),
            },
            {
                sizing: measurement.sizing ?? {},
                ...appearancePatch(measurement.appearance),
            }
        );
}

function mergePendingLocalById<T extends { id: string }>(
    scope: ProjectScope,
    remoteItems: T[],
    localItems: T[],
    isPendingWrite: (scope: ProjectScope, id: string) => boolean,
    isPendingDelete: (scope: ProjectScope, id: string) => boolean
): T[] {
    const visibleRemoteItems = remoteItems.filter((item) => !isPendingDelete(scope, item.id));
    const localById = new Map(localItems.map((item) => [item.id, item]));
    const mergedRemoteItems = visibleRemoteItems.map((item) => {
        const localItem = localById.get(item.id);
        if (localItem && isPendingWrite(scope, item.id)) {
            return localItem;
        }
        return item;
    });
    const remoteIds = new Set(visibleRemoteItems.map((item) => item.id));
    const pendingLocalItems = localItems.filter((item) => (
        !remoteIds.has(item.id) && isPendingWrite(scope, item.id)
    ));

    return [...mergedRemoteItems, ...pendingLocalItems];
}

export function mergeCostumePiecesWithPendingLocal(
    scope: ProjectScope,
    remotePieces: CostumePiece[],
    localPieces: CostumePiece[]
): CostumePiece[] {
    return mergePendingLocalById(
        scope,
        remotePieces,
        localPieces,
        isPendingCostumePieceWrite,
        isPendingCostumePieceDelete
    );
}

function mergeMeasurementsWithPendingLocal(
    scope: ProjectScope,
    remoteMeasurements: MeasurementProfile[],
    localMeasurements: MeasurementProfile[]
): MeasurementProfile[] {
    const visibleRemoteMeasurements = remoteMeasurements.filter((measurement) => (
        !isPendingCostumeMeasurementDelete(scope, measurement.actorId)
    ));
    const localByActorId = new Map(localMeasurements.map((measurement) => [measurement.actorId, measurement]));
    const mergedRemoteMeasurements = visibleRemoteMeasurements.map((measurement) => {
        const localMeasurement = localByActorId.get(measurement.actorId);
        if (localMeasurement && isPendingCostumeMeasurementWrite(scope, measurement.actorId)) {
            return localMeasurement;
        }
        return measurement;
    });
    const remoteActorIds = new Set(visibleRemoteMeasurements.map((measurement) => measurement.actorId));
    const pendingLocalMeasurements = localMeasurements.filter((measurement) => (
        !remoteActorIds.has(measurement.actorId)
        && isPendingCostumeMeasurementWrite(scope, measurement.actorId)
    ));

    return [...mergedRemoteMeasurements, ...pendingLocalMeasurements];
}

function measurementFromSizingProfile(
    sizingProfile: CostumeSizingProfile,
    existingProfile?: MeasurementProfile
): MeasurementProfile {
    const sizing = sizingProfile.sizing || {};

    if (existingProfile) {
        return {
            ...omitAppearance(existingProfile),
            actorId: sizingProfile.actorId,
            sizing,
            ...appearancePatch(sizingProfile.appearance),
            lastUpdated: lastUpdatedFrom(sizingProfile.lastUpdated, existingProfile.lastUpdated),
        };
    }

    return {
        actorId: sizingProfile.actorId,
        silhouette: 'nonbinary',
        unitPreference: 'in',
        measurements: {},
        measurementNotes: {},
        sizing,
        ...appearancePatch(sizingProfile.appearance),
        allergies: [],
        notes: '',
        measurementDate: '',
        isComplete: undefined,
        completedBy: undefined,
        completedAt: undefined,
        lastUpdated: lastUpdatedFrom(sizingProfile.lastUpdated),
    };
}

function hasCostumeDepartmentMeasurementData(profile: MeasurementProfile): boolean {
    return Object.keys(profile.measurements ?? {}).length > 0
        || Object.keys(profile.measurementNotes ?? {}).length > 0
        || (profile.allergies ?? []).length > 0
        || Boolean(profile.notes)
        || Boolean(profile.measurementDate)
        || Boolean(profile.isComplete)
        || Boolean(profile.completedBy)
        || typeof profile.completedAt === 'number';
}

function lastUpdatedFrom(...values: Array<number | undefined>): number {
    const numericValues = values.filter((value): value is number => typeof value === 'number');
    return numericValues.length > 0 ? Math.max(...numericValues) : 0;
}

export function mergeMeasurementsWithSizingProfiles(
    scope: ProjectScope,
    remoteMeasurements: MeasurementProfile[],
    remoteSizingProfiles: CostumeSizingProfile[],
    localMeasurements: MeasurementProfile[]
): MeasurementProfile[] {
    const mergedMeasurements = mergeMeasurementsWithPendingLocal(scope, remoteMeasurements, localMeasurements);
    const localByActorId = new Map(localMeasurements.map((measurement) => [measurement.actorId, measurement]));
    const visibleRemoteSizingProfiles = remoteSizingProfiles.filter((sizingProfile) => (
        !isPendingCostumeSizingDelete(scope, sizingProfile.actorId)
    ));
    const sizingByActorId = new Map(visibleRemoteSizingProfiles.map((sizingProfile) => [sizingProfile.actorId, sizingProfile]));
    const composedActorIds = new Set<string>();

    const composedMeasurements: MeasurementProfile[] = mergedMeasurements.map((measurement) => {
        const localMeasurement = localByActorId.get(measurement.actorId);
        const sizingProfile = sizingByActorId.get(measurement.actorId);
        const pendingLocalSizing = Boolean(localMeasurement && isPendingCostumeSizingWrite(scope, measurement.actorId));
        composedActorIds.add(measurement.actorId);

        if (pendingLocalSizing && localMeasurement) {
            return {
                ...omitAppearance(measurement),
                sizing: localMeasurement.sizing ?? {},
                ...appearancePatch(localMeasurement.appearance),
                lastUpdated: lastUpdatedFrom(measurement.lastUpdated, localMeasurement.lastUpdated),
            };
        }

        return {
            ...omitAppearance(measurement),
            sizing: sizingProfile?.sizing ?? {},
            ...appearancePatch(sizingProfile?.appearance),
            lastUpdated: lastUpdatedFrom(measurement.lastUpdated, sizingProfile?.lastUpdated),
        };
    });

    for (const sizingProfile of visibleRemoteSizingProfiles) {
        if (composedActorIds.has(sizingProfile.actorId)) {
            continue;
        }

        const localMeasurement = localByActorId.get(sizingProfile.actorId);
        if (localMeasurement && isPendingCostumeSizingWrite(scope, sizingProfile.actorId)) {
            composedMeasurements.push(measurementFromSizingProfile({
                actorId: localMeasurement.actorId,
                sizing: localMeasurement.sizing ?? {},
                ...appearancePatch(localMeasurement.appearance),
                lastUpdated: localMeasurement.lastUpdated,
            }, localMeasurement));
        } else {
            composedMeasurements.push(measurementFromSizingProfile(sizingProfile, localMeasurement));
        }
    }

    return composedMeasurements;
}

function mergeFittingsWithPendingLocal(
    scope: ProjectScope,
    remoteFittings: Fitting[],
    localFittings: Fitting[]
): Fitting[] {
    return mergePendingLocalById(
        scope,
        remoteFittings,
        localFittings,
        isPendingCostumeFittingWrite,
        isPendingCostumeFittingDelete
    );
}

function mergeQuickChangesWithPendingLocal(
    scope: ProjectScope,
    remoteTransitions: QuickChangeTransition[],
    localTransitions: QuickChangeTransition[]
): QuickChangeTransition[] {
    return mergePendingLocalById(
        scope,
        remoteTransitions,
        localTransitions,
        isPendingCostumeQuickChangeWrite,
        isPendingCostumeQuickChangeDelete
    );
}

export function useCostumesSync(enabled = true) {
    const cloudEnabled = useAppStore(state => state.cloud.enabled);
    const orgId = useAppStore(state => state.cloud.orgId);
    const projectId = useAppStore(state => state.cloud.projectId);
    const isAuthInitialized = useAppStore(state => state.auth.initialized);
    const user = useAppStore(state => state.auth.user);

    const unsubRefs = useRef<unknown[]>([]);
    const remoteMeasurementsRef = useRef<MeasurementProfile[]>([]);
    const remoteSizingProfilesRef = useRef<CostumeSizingProfile[]>([]);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        for (const unsubscribe of unsubRefs.current) {
            if (isUnsubscribe(unsubscribe)) {
                unsubscribe();
            }
        }
        unsubRefs.current = [];
        remoteMeasurementsRef.current = [];
        remoteSizingProfilesRef.current = [];

        if (!enabled || !cloudEnabled || !orgId || !projectId || !isAuthInitialized || !user) {
            return;
        }

        const scope = { orgId, projectId };

        unsubRefs.current = [
            subscribeToCostumePieces(scope, (pieces) => {
                if (!mountedRef.current) return;
                const remoteIds = new Set(pieces.map((piece) => piece.id));
                clearAcknowledgedPendingCostumePieceDeletes(scope, remoteIds);
                useAppStore.setState(state => ({
                    costumes: {
                        ...state.costumes,
                        pieces: mergeCostumePiecesWithPendingLocal(scope, pieces, state.costumes.pieces || []),
                    },
                }));
                const localById = new Map((useAppStore.getState().costumes.pieces || []).map((piece) => [piece.id, piece]));
                for (const piece of pieces) {
                    const localPiece = localById.get(piece.id);
                    if (!localPiece || recordsMatchForPendingWrite(piece, localPiece)) {
                        clearPendingCostumePieceWrite(scope, piece.id);
                    }
                }
            }),
            subscribeToMeasurementProfiles(scope, (measurements) => {
                if (!mountedRef.current) return;
                remoteMeasurementsRef.current = measurements;
                const remoteIds = new Set(measurements.map((measurement) => measurement.actorId));
                clearAcknowledgedPendingCostumeMeasurementDeletes(scope, remoteIds);
                useAppStore.setState(state => ({
                    costumes: {
                        ...state.costumes,
                        measurements: mergeMeasurementsWithSizingProfiles(
                            scope,
                            remoteMeasurementsRef.current,
                            remoteSizingProfilesRef.current,
                            state.costumes.measurements || []
                        ),
                    },
                }));
                const localByActorId = new Map(
                    (useAppStore.getState().costumes.measurements || []).map((measurement) => [
                        measurement.actorId,
                        measurement,
                    ])
                );
                for (const measurement of measurements) {
                    const localMeasurement = localByActorId.get(measurement.actorId);
                    if (
                        !localMeasurement
                        || remoteTimestampHasCaughtUp(measurement.lastUpdated, localMeasurement.lastUpdated)
                        || recordsMatchForPendingWrite(measurement, localMeasurement)
                    ) {
                        clearPendingCostumeMeasurementWrite(scope, measurement.actorId);
                    }
                }
            }),
            subscribeToCostumeSizingProfiles(scope, (sizingProfiles) => {
                if (!mountedRef.current) return;
                remoteSizingProfilesRef.current = sizingProfiles;
                const remoteIds = new Set(sizingProfiles.map((sizingProfile) => sizingProfile.actorId));
                clearAcknowledgedPendingCostumeSizingDeletes(scope, remoteIds);
                useAppStore.setState(state => ({
                    costumes: {
                        ...state.costumes,
                        measurements: mergeMeasurementsWithSizingProfiles(
                            scope,
                            remoteMeasurementsRef.current,
                            remoteSizingProfilesRef.current,
                            state.costumes.measurements || []
                        ),
                    },
                }));
                const localByActorId = new Map(
                    (useAppStore.getState().costumes.measurements || []).map((measurement) => [
                        measurement.actorId,
                        measurement,
                    ])
                );
                for (const sizingProfile of sizingProfiles) {
                    const localMeasurement = localByActorId.get(sizingProfile.actorId);
                    if (sizingProfileAcknowledgesMeasurement(sizingProfile, localMeasurement)) {
                        clearPendingCostumeSizingWrite(scope, sizingProfile.actorId);
                    }
                }
            }),
            subscribeToFittings(scope, (fittings) => {
                if (!mountedRef.current) return;
                const remoteIds = new Set(fittings.map((fitting) => fitting.id));
                clearAcknowledgedPendingCostumeFittingDeletes(scope, remoteIds);
                useAppStore.setState(state => ({
                    costumes: {
                        ...state.costumes,
                        fittings: mergeFittingsWithPendingLocal(scope, fittings, state.costumes.fittings || []),
                    },
                }));
                const localById = new Map((useAppStore.getState().costumes.fittings || []).map((fitting) => [fitting.id, fitting]));
                for (const fitting of fittings) {
                    const localFitting = localById.get(fitting.id);
                    if (!localFitting || recordsMatchForPendingWrite(fitting, localFitting)) {
                        clearPendingCostumeFittingWrite(scope, fitting.id);
                    }
                }
            }),
            subscribeToQuickChangeTransitions(scope, (quickChangeTransitions) => {
                if (!mountedRef.current) return;
                const remoteIds = new Set(quickChangeTransitions.map((transition) => transition.id));
                clearAcknowledgedPendingCostumeQuickChangeDeletes(scope, remoteIds);
                useAppStore.setState(state => ({
                    costumes: {
                        ...state.costumes,
                        quickChangeTransitions: mergeQuickChangesWithPendingLocal(
                            scope,
                            quickChangeTransitions,
                            state.costumes.quickChangeTransitions || []
                        ),
                    },
                }));
                const localById = new Map(
                    (useAppStore.getState().costumes.quickChangeTransitions || []).map((transition) => [
                        transition.id,
                        transition,
                    ])
                );
                for (const transition of quickChangeTransitions) {
                    const localTransition = localById.get(transition.id);
                    if (!localTransition || recordsMatchForPendingWrite(transition, localTransition)) {
                        clearPendingCostumeQuickChangeWrite(scope, transition.id);
                    }
                }
            }),
        ];

        return () => {
            for (const unsubscribe of unsubRefs.current) {
                if (isUnsubscribe(unsubscribe)) {
                    unsubscribe();
                }
            }
            unsubRefs.current = [];
        };
    }, [cloudEnabled, enabled, orgId, projectId, isAuthInitialized, user]);
}

export function useOwnCostumeSizingSync(actorId: string | undefined) {
    const cloudEnabled = useAppStore(state => state.cloud.enabled);
    const orgId = useAppStore(state => state.cloud.orgId);
    const projectId = useAppStore(state => state.cloud.projectId);
    const isAuthInitialized = useAppStore(state => state.auth.initialized);
    const user = useAppStore(state => state.auth.user);

    const unsubRef = useRef<unknown>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        if (isUnsubscribe(unsubRef.current)) {
            unsubRef.current();
        }
        unsubRef.current = null;

        if (!cloudEnabled || !orgId || !projectId || !isAuthInitialized || !user || !actorId) {
            return;
        }

        const scope = { orgId, projectId };
        unsubRef.current = subscribeToOwnCostumeSizingProfile(scope, actorId, (sizingProfile) => {
            if (!mountedRef.current) return;

            useAppStore.setState(state => {
                const currentMeasurements = state.costumes.measurements || [];
                const existingProfile = currentMeasurements.find((measurement) => measurement.actorId === actorId);

                if (!sizingProfile) {
                    if (existingProfile && isPendingCostumeSizingWrite(scope, actorId)) {
                        return state;
                    }

                    if (existingProfile && hasCostumeDepartmentMeasurementData(existingProfile)) {
                        return {
                            costumes: {
                                ...state.costumes,
                                measurements: currentMeasurements.map((measurement) => (
                                    measurement.actorId === actorId
                                        ? { ...omitAppearance(measurement), sizing: {} }
                                        : measurement
                                )),
                            },
                        };
                    }

                    return {
                        costumes: {
                            ...state.costumes,
                            measurements: currentMeasurements.filter((measurement) => measurement.actorId !== actorId),
                        },
                    };
                }

                if (
                    existingProfile
                    && isPendingCostumeSizingWrite(scope, actorId)
                    && !sizingProfileAcknowledgesMeasurement(sizingProfile, existingProfile)
                ) {
                    return state;
                }

                const nextProfile = measurementFromSizingProfile(sizingProfile, existingProfile);
                const replaced = currentMeasurements.some((measurement) => measurement.actorId === actorId);
                return {
                    costumes: {
                        ...state.costumes,
                        measurements: replaced
                            ? currentMeasurements.map((measurement) => (
                                measurement.actorId === actorId ? nextProfile : measurement
                            ))
                            : [...currentMeasurements, nextProfile],
                    },
                };
            });

            const localMeasurement = useAppStore.getState().costumes.measurements.find(
                (measurement) => measurement.actorId === actorId
            );
            if (sizingProfile && sizingProfileAcknowledgesMeasurement(sizingProfile, localMeasurement)) {
                clearPendingCostumeSizingWrite(scope, actorId);
            }
        });

        return () => {
            if (isUnsubscribe(unsubRef.current)) {
                unsubRef.current();
            }
            unsubRef.current = null;
        };
    }, [actorId, cloudEnabled, isAuthInitialized, orgId, projectId, user]);
}
